import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasteableInput } from "@/components/PasteableInput";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CalendarBlank, CaretDown, Check } from "@phosphor-icons/react";
import { toast } from "sonner";

const todayISO = () => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };
const toISO = (d) => new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
const fromISO = (s) => { const d = new Date(s + "T12:00:00"); return isNaN(d) ? new Date() : d; };
const fmt = (n, cur) => `${cur === "USD" ? "$" : "MX$"}${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Reusable expense form dialog.
 * Props:
 *   open: bool
 *   onOpenChange: (bool) => void
 *   onSaved: () => void  // called after a successful save
 *   lockBank?: { id, name, currency }  // when set, bank is preselected and locked
 *                                       // (and currency is forced to bank.currency)
 *   existing?: expense doc  // when set, edits instead of creates and requires a
 *                            // justification (reason). On save calls PUT /expenses/{id}.
 */
export default function ExpenseFormDialog({ open, onOpenChange, onSaved, lockBank = null, existing = null }) {
  const isEdit = !!existing;
  const initial = () => isEdit ? ({
    category_id: existing.category_id || "",
    title: existing.title || "",
    amount: String(existing.amount ?? ""),
    currency: existing.currency || "MXN",
    date: existing.date || todayISO(),
    bank_id: existing.bank_id || "",
    vendor: existing.vendor || "",
    note: existing.note || "",
    edit_reason: "",
  }) : ({
    category_id: "",
    title: "",
    amount: "",
    currency: lockBank ? lockBank.currency : "MXN",
    date: todayISO(),
    bank_id: lockBank ? lockBank.id : "",
    vendor: "",
    note: "",
  });
  const [form, setForm] = useState(initial());
  const [categories, setCategories] = useState([]);
  const [banks, setBanks] = useState([]);
  const [saving, setSaving] = useState(false);
  const [catOpen, setCatOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(initial());
    Promise.all([api.get("/expense-categories"), api.get("/banks")])
      .then(([c, b]) => { setCategories(c.data); setBanks(b.data); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, lockBank?.id, existing?.id]);

  // Banks may come stripped (only id + name) for non-admin users with the
  // `add_expenses` capability. In that case show all banks and only the name.
  const banksHaveCurrency = banks.some((b) => b.currency);
  const banksForCurrency = banksHaveCurrency
    ? banks.filter((b) => b.currency === form.currency)
    : banks;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.category_id) return toast.error("Selecciona una categoría");
    if (!form.amount || parseFloat(form.amount) <= 0) return toast.error("Monto inválido");
    if (isEdit && !(form.edit_reason || "").trim()) {
      return toast.error("La justificación es requerida para editar");
    }
    setSaving(true);
    try {
      const payload = {
        category_id: form.category_id,
        title: form.title,
        amount: parseFloat(form.amount),
        currency: form.currency,
        date: form.date,
        bank_id: form.bank_id || null,
        vendor: form.vendor,
        note: form.note,
      };
      if (isEdit) {
        payload.edit_reason = form.edit_reason.trim();
        await api.put(`/expenses/${existing.id}`, payload);
        toast.success("Gasto actualizado");
      } else {
        await api.post("/expenses", payload);
        toast.success("Gasto registrado");
      }
      onOpenChange(false);
      onSaved && onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="expense-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            {isEdit ? "Editar gasto" : `Registrar gasto${lockBank ? ` · ${lockBank.name}` : ""}`}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider">Título (¿qué fue?) *</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Ej. Renta julio, Tóner impresora, Comida con cliente..."
              className="mt-1"
              data-testid="exp-title"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider">Categoría *</Label>
              <Popover open={catOpen} onOpenChange={setCatOpen}>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={catOpen}
                    className="w-full mt-1 justify-between font-normal"
                    data-testid="exp-cat"
                  >
                    <span className={form.category_id ? "" : "text-zinc-500"}>
                      {form.category_id
                        ? (categories.find((c) => c.id === form.category_id)?.name || "Selecciona...")
                        : "Selecciona categoría..."}
                    </span>
                    <CaretDown size={14} className="text-zinc-500 ml-2 shrink-0" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Escribe para buscar..." data-testid="exp-cat-search" />
                    <CommandList>
                      <CommandEmpty>Sin coincidencias.</CommandEmpty>
                      <CommandGroup>
                        {categories.map((c) => (
                          <CommandItem
                            key={c.id}
                            value={c.name}
                            onSelect={() => {
                              setForm((f) => ({ ...f, category_id: c.id }));
                              setCatOpen(false);
                            }}
                            data-testid={`exp-cat-opt-${c.id}`}
                          >
                            <Check size={14} className={`mr-2 ${form.category_id === c.id ? "opacity-100" : "opacity-0"}`} />
                            {c.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Moneda</Label>
              <Select
                value={form.currency}
                onValueChange={(v) => setForm({ ...form, currency: v, bank_id: lockBank ? form.bank_id : "" })}
                disabled={!!lockBank}
              >
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="MXN">MXN</SelectItem><SelectItem value="USD">USD</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Monto *</Label>
              <Input type="number" step="0.01" min="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required className="mt-1 numeric" data-testid="exp-amount" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider">Fecha</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" className="w-full mt-1 justify-start gap-2 font-normal">
                    <CalendarBlank size={14} className="text-zinc-500" /> {form.date}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={fromISO(form.date)} onSelect={(d) => { if (d) setForm({ ...form, date: toISO(d) }); }} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider">
                Banco {lockBank ? "(fijo)" : "(opcional — descuenta del saldo)"}
              </Label>
              {lockBank ? (
                <div className="mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded text-sm">
                  <b>{lockBank.name}</b> · {lockBank.currency}
                </div>
              ) : (
                <Select value={form.bank_id || "none"} onValueChange={(v) => setForm({ ...form, bank_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Sin banco" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Sin banco (solo registro) —</SelectItem>
                    {banksForCurrency.map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.name}
                        {b.currency && ` (${b.currency}${b.balance !== undefined ? ` · ${fmt(b.balance, b.currency)}` : ""})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Proveedor</Label>
              <PasteableInput value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} placeholder="A quién le pagaste" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Nota</Label>
              <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Detalle adicional" className="mt-1" />
            </div>
          </div>
          {isEdit && (
            <div className="border border-amber-300 bg-amber-50 rounded p-3">
              <Label className="text-xs uppercase tracking-wider text-amber-900">
                Justificación * <span className="font-normal normal-case">(¿por qué editas este gasto?)</span>
              </Label>
              <Input
                value={form.edit_reason}
                onChange={(e) => setForm({ ...form, edit_reason: e.target.value })}
                placeholder="Ej. Cambié el monto porque me dieron descuento..."
                className="mt-1 bg-white"
                data-testid="exp-edit-reason"
                required
              />
              <p className="text-[10px] text-amber-700 mt-1">Se guardará en el historial del gasto para auditoría.</p>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving} className="bg-[#002FA7] hover:bg-[#002277] text-white" data-testid="exp-submit">
              {saving ? "Guardando…" : isEdit ? "Guardar cambios" : "Registrar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
